import { ChangeDetectorRef, Component } from '@angular/core'
import { SafePipe } from '@app/shared/pipe/safe.pipe'
import { AuthService } from '@core/service/auth.service'
import { OutputTemplate } from '@app/features/scope-overview/model/output-template.model'
import { User } from '@core/model/user.model'
import { CompanyManagementService } from '@app/features/company-management/service/company-management.service'
import { ActivatedRoute, Router } from '@angular/router'
import { BehaviorSubject, debounceTime, map } from 'rxjs'
import { skip } from 'rxjs/operators'
import { untilDestroyed } from '@shared/utils/utils'
import { ExportModalComponent } from '@app/features/scope-overview/components/export-modal/export-modal.component'
import { ExportModalConfig } from '@app/features/scope-overview/model/export-modal-config'
import { LanguageService } from '@app/core/service/language.service'
import { ParentChildOutputWhitelistInfo } from '@app/features/scope-overview/components/change-scope-output-modal/change-scope-output-modal.component'
import { OutputField } from '@app/features/scope-overview/model/output-field.model'
import { SNACKBAR_LENGTH_LONG, SnackbarEventType, SnackbarService } from '@shared/utils/snackbar.service'
import { MatDialog, MatDialogRef } from '@angular/material/dialog'
import { PreviewOutputComponent } from '@shared/components/preview-output/preview-output.component'
import { UploadType } from '@app/shared/components/upload-modal/models/upload-modal-config'
import { ScopeDynamicFieldSettingModel } from '@app/features/scoping/models/scope-dynamic-field-setting.model'
import { ScenarioService } from '@app/features/company-management/service/scenario.service'
import { ScenarioQuestion } from '@app/features/scope-overview/model/scenario-question.model'
import { Timeout } from '@app/core/model/enums/timeout.enum'
import { UploadModalComponent } from '@app/shared/components/upload-modal/upload-modal.component'

@Component({
  selector: 'document-template',
  templateUrl: './document-template.component.html',
  styleUrls: ['./document-template.component.scss'],
  providers: [SafePipe],
})
export class DocumentTemplateComponent {
  private readonly destroy$
  id!: number
  outputTemplate: OutputTemplate
  loggedInUser: User
  companyDynamicFieldSettings: ScopeDynamicFieldSettingModel[]
  scenarioFields: ScenarioQuestion[]
  loading = true
  requestSubject: BehaviorSubject<{ template: OutputTemplate; download: boolean }>
  templateWhitelist: ParentChildOutputWhitelistInfo
  updateInProgress: boolean

  constructor(
    private authService: AuthService,
    private manageService: CompanyManagementService,
    private route: ActivatedRoute,
    private cdr: ChangeDetectorRef,
    private router: Router,
    private snackbarService: SnackbarService,
    private dialog: MatDialog,
    private scenarioService: ScenarioService,
    private lang: LanguageService
  ) {
    this.destroy$ = untilDestroyed()
    this.loggedInUser = this.authService.loggedInUser
    this.id = parseInt(route.snapshot.queryParamMap.get('id'))
    this.retrieveOutputTemplate()
    this.retrieveWhitelist()
    this.retrieveCompanyDynamicFields()
    this.retrieveCompanyScenarioFields()
  }

  retrieveOutputTemplate() {
    this.manageService.getWordTemplate(this.id).subscribe((template) => {
      this.outputTemplate = template
      this.loading = false
      this.cdr.detectChanges()
      this.handleRequests()
    })
  }

  retrieveCompanyDynamicFields() {
    this.manageService.retrieveCompanyDynamicFields().subscribe((dynamicFields) => {
      this.companyDynamicFieldSettings = dynamicFields
    })
  }

  retrieveCompanyScenarioFields() {
    this.scenarioService.getAll(true).subscribe((scenario) => {
      this.scenarioFields = scenario.flatMap((s) => s.categories).flatMap((c) => c.questions)
    })
  }

  handleRequests() {
    this.requestSubject = new BehaviorSubject<{ template: OutputTemplate; download: boolean }>({
      template: this.outputTemplate,
      download: false,
    })
    this.requestSubject
      .pipe(
        this.destroy$(),
        skip(1),
        debounceTime(Timeout.M),
        map((request: { template: OutputTemplate; download: boolean }) => {
          return request.download ? this.updateAndDownload(request.template) : this.sendUpdateRequest(request.template)
        })
      )
      .subscribe()
  }

  retrieveWhitelist() {
    this.manageService.getParentOutputTemplatesWhitelist(this.loggedInUser.company.id).subscribe((whitelist) => {
      this.templateWhitelist = whitelist
    })
  }

  public sendUpdateRequest(template: OutputTemplate) {
    return this.manageService.updateCompanyCustomOutputTemplate(template).subscribe({
      next: () => {
        this.updateInProgress = false
        this.cdr.detectChanges()
      },
    })
  }

  private openExportModal() {
    this.updateInProgress = false
    this.cdr.detectChanges()

    const dialogConfig: ExportModalConfig = {
      title: `Export ${this.lang.get('doc_template')}`,
      body: `Select which document file you want to output your ${this.lang.get('doc_template|l')}:`,
      currentUser: this.loggedInUser,
      limitModalWidth: true,
      skipWhenSingle: true,
      outputTemplate: this.outputTemplate,
    }

    this.dialog.open(ExportModalComponent, { data: dialogConfig })
  }

  private updateAndDownload(template: OutputTemplate) {
    if (this.outputTemplate.company.id !== this.loggedInUser.company.id) {
      return this.openExportModal()
    } else {
      return this.manageService.updateCompanyCustomOutputTemplate(template).subscribe({
        next: () => {
          this.openExportModal()
        },
      })
    }
  }

  updateTemplate(template: OutputTemplate, download = false) {
    this.requestSubject.next({ template, download })
  }

  deleteField(field: OutputField) {
    this.outputTemplate.outputFields.splice(this.outputTemplate.outputFields.indexOf(field), 1)
    this.requestSubject.next({ template: this.outputTemplate, download: false })
  }

  uploadBaseDocx() {
    const dialog = this.dialog.open(UploadModalComponent, {
      data: {
        title: `Upload Docx Base Template`,
        body: `Upload Docx Base Template`,
        type: UploadType.WORD_OUTPUT,
        verifyService: this.manageService,
        submitFn: (file: File) => this.manageService.uploadCustomTemplateDocxBaseTemplate(this.id, file),
        successFn: (result: OutputTemplate) => {
          dialog.close()
          this.outputTemplate = result
          this.cdr.detectChanges()
        },
        showRemoveOption: this.outputTemplate.baseDocxTemplate,
        removeOptionText: 'To revert to default base template:',
        removeFn: () => {
          this.manageService.removeCustomTemplateDocxBaseTemplate(this.id).subscribe({
            next: (template: OutputTemplate) => {
              dialog.close()
              this.outputTemplate.baseDocxTemplate = null
              this.outputTemplate.outputFields = template.outputFields
              this.cdr.detectChanges()
            },
          })
        },
      },
    })
  }

  previewExport() {
    const dialog = this.dialog.open(PreviewOutputComponent, {
      maxWidth: '100vw',
      maxHeight: '100vh',
      height: '100%',
      width: '100%',
    })

    dialog.afterClosed().subscribe(() => document.body.classList.remove('cdk-global-scrollblock'))
    document.body.classList.add('cdk-global-scrollblock')
    dialog.componentInstance.outputTemplateName = this.outputTemplate.name
    dialog.componentInstance.templateLanguage = this.outputTemplate.language

    dialog.componentInstance.onDownloadExport.subscribe(() => this.openExportModal())

    if (this.outputTemplate.company.id !== this.loggedInUser.company.id) this.getPreviewExport(dialog)
    else
      this.manageService.updateCompanyCustomOutputTemplate(this.outputTemplate).subscribe(() => {
        this.getPreviewExport(dialog)
      })
  }

  getPreviewExport(dialog: MatDialogRef<PreviewOutputComponent, any>) {
    this.manageService.previewExport(this.outputTemplate.id).subscribe({
      next: (response) => {
        dialog.componentInstance.documentPreview = response
      },
      error: () => {
        this.snackbarService.showDefaultErrorSnackbar()
        dialog.componentInstance.loading = false
        dialog.componentInstance.errorState = true
      },
    })
  }

  duplicate(name: string) {
    this.manageService.duplicateCompanyCustomOutputTemplate(this.outputTemplate.id, name).subscribe({
      next: () => {
        this.snackbarService.showSnackbar(
          `Template ${this.outputTemplate.name} successfully duplicated`,
          SNACKBAR_LENGTH_LONG,
          SnackbarEventType.SUCCESS
        )
        this.backToGallery()
      },
    })
  }

  backToGallery() {
    this.router.navigate([], { relativeTo: this.route, queryParams: { tab: 'output', type: 'document' } })
  }
}
